package com.exura.features.import

import com.exura.features.localization.domain.model.BasicLangSet
import com.exura.features.localization.domain.model.Language
import com.exura.features.localization.domain.model.Localization

interface ImportLang {
    val accessCode: AccessCode
    val demoSets: DemoSets
    val publishing: Publishing
    val partnership: Partnership
    val dialogs: Dialogs
    val errors: Errors
    val success: Success

    val importPlans: String

    interface AccessCode {
        val enterAccessCode: String
        val enterAccessCodeDescription: String
    }

    interface DemoSets {
        val useAvailableDemoSets: String
        val noDemoSetsAvailable: String
        val plansCount: (Int) -> String
        val failedToLoadDemoSets: String
    }

    interface Publishing {
        val publishPlan: String
        val stepChoosePlan: String
        val selectPlanToPublish: String
        val selectPlan: String
        val stepCheckChoice: String
        val makeSureOfPlanSelection: String
        val noPlanSelected: String
        val stepEnterDemoId: String
        val enterDemoSetId: String
        val demoSetIdPlaceholder: String
        val makePrivate: String
        val stepPublish: String
        val planWillBeAvailable: String
        val publishButton: String
        val fillAllFields: String
    }

    interface Partnership {
        val partnershipProgramTitle: String
        val partnershipProgramSubtitle: String
        val partnershipStep1Title: String
        val partnershipStep1Description: String
        val partnershipStep2Title: String
        val partnershipStep2Description: String
        val partnershipStep3Title: String
        val partnershipStep3Description: String
        val partnershipStep4Title: String
        val partnershipStep4Description: String
        val partnershipContactMessage: String
        val partnershipContactEmail: String
        val partnershipBottomMessage: String
    }

    interface Dialogs {
        val planSelectionDialogTitle: String
        val errorLoadingPlans: String
        val tryAgain: String
        val noPlansAvailable: String
    }

    interface Errors {
        val importError: String
        val publicationError: String
    }

    interface Success {
        val importedPlansCount: (Int) -> String
        val demoSetImportedWithCount: (String, Int) -> String
        val planPublishedSuccessfully: String
    }

}

data object ImportPolish : ImportLang {
    override val accessCode = AccessCode
    override val demoSets = DemoSets
    override val publishing = Publishing
    override val partnership = Partnership
    override val dialogs = Dialogs
    override val errors = Errors
    override val success = Success

    override val importPlans: String = "Importuj plany"

    data object AccessCode : ImportLang.AccessCode {
        override val enterAccessCode: String = "Wprowadź kod dostępu"
        override val enterAccessCodeDescription: String = "Wprowadź kod dostępu do prywatnego zestawu demonstracyjnego"
    }

    data object DemoSets : ImportLang.DemoSets {
        override val useAvailableDemoSets: String = "Użyj dostępnych zestawów demonstracyjnych"
        override val noDemoSetsAvailable: String = "Brak dostępnych zestawów demonstracyjnych"
        override val plansCount: (Int) -> String = { count -> "$count planów" }
        override val failedToLoadDemoSets: String = "Nie udało się załadować zestawów demonstracyjnych"
    }

    data object Publishing : ImportLang.Publishing {
        override val publishPlan: String = "Opublikuj plan"
        override val stepChoosePlan: String = "Wybierz plan"
        override val selectPlanToPublish: String = "Wybierz plan do opublikowania"
        override val selectPlan: String = "Wybierz plan"
        override val stepCheckChoice: String = "Sprawdź wybór"
        override val makeSureOfPlanSelection: String = "Upewnij się, że wybrałeś właściwy plan"
        override val noPlanSelected: String = "Nie wybrano planu"
        override val stepEnterDemoId: String = "Wprowadź ID zestawu"
        override val enterDemoSetId: String = "Wprowadź ID zestawu demonstracyjnego"
        override val demoSetIdPlaceholder: String = "ID zestawu demonstracyjnego"
        override val makePrivate: String = "Uczyń prywatnym"
        override val stepPublish: String = "Opublikuj"
        override val planWillBeAvailable: String = "Plan będzie dostępny w wybranym zestawie"
        override val publishButton: String = "Opublikuj"
        override val fillAllFields: String = "Wypełnij wszystkie pola"
    }

    data object Partnership : ImportLang.Partnership {
        override val partnershipProgramTitle: String = "Program partnerski"
        override val partnershipProgramSubtitle: String =
            "Dołącz do naszego programu partnerskiego i zarabiaj na udostępnianiu swoich planów"
        override val partnershipStep1Title: String = "Stwórz plany"
        override val partnershipStep1Description: String = "Stwórz wysokiej jakości plany leczenia"
        override val partnershipStep2Title: String = "Opublikuj plany"
        override val partnershipStep2Description: String =
            "Opublikuj swoje plany w publicznych zestawach demonstracyjnych"
        override val partnershipStep3Title: String = "Promuj swoje plany"
        override val partnershipStep3Description: String = "Udostępniaj linki do swoich planów klientom"
        override val partnershipStep4Title: String = "Zarabiaj"
        override val partnershipStep4Description: String = "Otrzymuj prowizję za każde użycie Twoich planów"
        override val partnershipContactMessage: String = "Skontaktuj się z nami:"
        override val partnershipContactEmail: String = "kontakt@exura.app"
        override val partnershipBottomMessage: String = "Dołącz już dziś i zacznij zarabiać na swoich planach!"
    }

    data object Dialogs : ImportLang.Dialogs {
        override val planSelectionDialogTitle: String = "Wybór planu"
        override val errorLoadingPlans: String = "Błąd podczas ładowania planów"
        override val tryAgain: String = "Spróbuj ponownie"
        override val noPlansAvailable: String = "Brak dostępnych planów"
    }

    data object Errors : ImportLang.Errors {
        override val importError: String = "Błąd importu"
        override val publicationError: String = "Błąd publikacji"
    }

    data object Success : ImportLang.Success {
        override val importedPlansCount: (Int) -> String = { count -> "Zaimportowano $count planów" }
        override val demoSetImportedWithCount: (String, Int) -> String =
            { name, count -> "Zaimportowano $name ($count planów)" }
        override val planPublishedSuccessfully: String = "Plan opublikowany pomyślnie"
    }
}

data object ImportEnglish : ImportLang {
    override val accessCode = AccessCode
    override val demoSets = DemoSets
    override val publishing = Publishing
    override val partnership = Partnership
    override val dialogs = Dialogs
    override val errors = Errors
    override val success = Success

    override val importPlans: String = "Import plans"

    data object AccessCode : ImportLang.AccessCode {
        override val enterAccessCode: String = "Enter access code"
        override val enterAccessCodeDescription: String = "Enter access code for private demo set"
    }

    data object DemoSets : ImportLang.DemoSets {
        override val useAvailableDemoSets: String = "Use available demo sets"
        override val noDemoSetsAvailable: String = "No demo sets available"
        override val plansCount: (Int) -> String = { count -> "$count plans" }
        override val failedToLoadDemoSets: String = "Failed to load demo sets"
    }

    data object Publishing : ImportLang.Publishing {
        override val publishPlan: String = "Publish plan"
        override val stepChoosePlan: String = "Choose plan"
        override val selectPlanToPublish: String = "Select plan to publish"
        override val selectPlan: String = "Select plan"
        override val stepCheckChoice: String = "Check choice"
        override val makeSureOfPlanSelection: String = "Make sure of plan selection"
        override val noPlanSelected: String = "No plan selected"
        override val stepEnterDemoId: String = "Enter demo ID"
        override val enterDemoSetId: String = "Enter demo set ID"
        override val demoSetIdPlaceholder: String = "Demo set ID"
        override val makePrivate: String = "Make private"
        override val stepPublish: String = "Publish"
        override val planWillBeAvailable: String = "Plan will be available in selected set"
        override val publishButton: String = "Publish"
        override val fillAllFields: String = "Fill all fields"
    }

    data object Partnership : ImportLang.Partnership {
        override val partnershipProgramTitle: String = "Partnership program"
        override val partnershipProgramSubtitle: String =
            "Join our partnership program and earn money by sharing your plans"
        override val partnershipStep1Title: String = "Create plans"
        override val partnershipStep1Description: String = "Create high-quality treatment plans"
        override val partnershipStep2Title: String = "Publish plans"
        override val partnershipStep2Description: String = "Publish your plans in public demo sets"
        override val partnershipStep3Title: String = "Promote your plans"
        override val partnershipStep3Description: String = "Share links to your plans with clients"
        override val partnershipStep4Title: String = "Earn money"
        override val partnershipStep4Description: String = "Receive commission for each use of your plans"
        override val partnershipContactMessage: String = "Contact us:"
        override val partnershipContactEmail: String = "kontakt@exura.app"
        override val partnershipBottomMessage: String = "Join today and start earning from your plans!"
    }

    data object Dialogs : ImportLang.Dialogs {
        override val planSelectionDialogTitle: String = "Plan selection"
        override val errorLoadingPlans: String = "Error loading plans"
        override val tryAgain: String = "Try again"
        override val noPlansAvailable: String = "No plans available"
    }

    data object Errors : ImportLang.Errors {
        override val importError: String = "Import error"
        override val publicationError: String = "Publication error"
    }

    data object Success : ImportLang.Success {
        override val importedPlansCount: (Int) -> String = { count -> "Imported $count plans" }
        override val demoSetImportedWithCount: (String, Int) -> String =
            { name, count -> "Imported $name ($count plans)" }
        override val planPublishedSuccessfully: String = "Plan published successfully"
    }
}

private val importLangSet = BasicLangSet<ImportLang>(
    mapOf(
        Language.POLISH to ImportPolish,
        Language.ENGLISH to ImportEnglish,
    )
)

val importLocalization = Localization<ImportLang>(importLangSet)

/*
USAGE:
import com.exura.features.import.importLocalization

fun someFunction() {
    println(importLocalization.current.accessCode.enterAccessCode)
}
*/